import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
    Lock, Loader2, RotateCcw, Send, CheckCircle, Navigation, FileEdit,
} from 'lucide-react';

import CustomDatePicker from '../../components/CustomDatePicker';
import { inputCls, Label, SelectIcon, SectionHeader } from '../../components/vendor/sharedVendorUI';
import { formatIndianCurrency } from '../../utilities/amountToTextHelper';

import {
    fetchSPPOCCforClose, fetchVendorsForSPPOClose, fetchPOForSPPOClose, fetchSPPOByNoForClose,
    closeSPPO, clearVendors, clearPONumbers, clearPODetails, clearSaveResult, resetSPPOClose,
    selectCostCentersArray, selectCostCentersLoading,
    selectVendorsArray, selectVendorsLoading,
    selectPONumbersArray, selectPONumbersLoading,
    selectPODetails, selectPODetailsLoading,
    selectSaveStatus, selectSaveLoading, selectSaveError,
} from '../../slices/spPOSlice/sppoCloseCreationSlice';

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    const d = val instanceof Date ? val : new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const parseAPIDate = (val) => {
    if (!val) return null;
    const m = /^(\d{2})-([A-Za-z]{3})-(\d{4})$/.exec(val);
    if (m) {
        const [, dd, mon, yyyy] = m;
        const idx = MONTH_ABBR.findIndex((x) => x.toLowerCase() === mon.toLowerCase());
        if (idx >= 0) return new Date(parseInt(yyyy, 10), idx, parseInt(dd, 10));
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
};

const SPPOClose = ({ menuData }) => {
    const dispatch = useDispatch();
    const { userData } = useSelector((s) => s.auth);
    const userId = userData?.userId || userData?.UID || userData?.employeeId || '';
    const roleId = userData?.roleId || userData?.RID || 0;
    const userName = userData?.userName || userData?.UserName || 'system';

    const costCenters = useSelector(selectCostCentersArray);
    const ccLoading = useSelector(selectCostCentersLoading);
    const vendors = useSelector(selectVendorsArray);
    const vendorsLoading = useSelector(selectVendorsLoading);
    const poNumbers = useSelector(selectPONumbersArray);
    const poNumbersLoading = useSelector(selectPONumbersLoading);
    const poDetails = useSelector(selectPODetails);
    const poDetailsLoading = useSelector(selectPODetailsLoading);
    const saveStatus = useSelector(selectSaveStatus);
    const saveLoading = useSelector(selectSaveLoading);
    const saveError = useSelector(selectSaveError);

    const [ccCode, setCcCode] = useState('');
    const [vendorCode, setVendorCode] = useState('');
    const [sppoNo, setSppoNo] = useState('');
    const [closeDate, setCloseDate] = useState(null);
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (userId) dispatch(fetchSPPOCCforClose({ userId, roleId }));
        return () => { dispatch(resetSPPOClose()); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        setVendorCode(''); setSppoNo('');
        dispatch(clearVendors()); dispatch(clearPONumbers()); dispatch(clearPODetails());
        if (ccCode) dispatch(fetchVendorsForSPPOClose(ccCode));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ccCode]);

    useEffect(() => {
        setSppoNo('');
        dispatch(clearPONumbers()); dispatch(clearPODetails());
        if (vendorCode && ccCode) dispatch(fetchPOForSPPOClose({ vendorCode, ccCode }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorCode]);

    useEffect(() => {
        setCloseDate(null); setRemarks('');
        dispatch(clearPODetails());
        if (sppoNo && ccCode && vendorCode) dispatch(fetchSPPOByNoForClose({ sppono: sppoNo, ccCode, vendorCode }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sppoNo]);

    useEffect(() => {
        if (saveStatus === 'success') {
            toast.success('SPPO Closed Successfully.');
            dispatch(clearSaveResult());
            handleFullReset();
        } else if (saveStatus === 'failed' && saveError) {
            toast.error(saveError);
            dispatch(clearSaveResult());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saveStatus]);

    const handleFullReset = () => {
        setCcCode(''); setVendorCode(''); setSppoNo(''); setCloseDate(null); setRemarks('');
        dispatch(resetSPPOClose());
        if (userId) dispatch(fetchSPPOCCforClose({ userId, roleId }));
    };

    const handleSubmit = () => {
        if (!ccCode) { toast.warn('Select Cost Center.'); return; }
        if (!vendorCode) { toast.warn('Select Vendor.'); return; }
        if (!sppoNo) { toast.warn('Select SPPO No.'); return; }
        if (!closeDate) { toast.warn('Select Closing Date.'); return; }
        if (!remarks.trim()) { toast.warn('Enter Remarks.'); return; }

        dispatch(closeSPPO({
            SPPONo: sppoNo,
            VendorCode: vendorCode,
            CCCode: ccCode,
            POCloseRemarks: remarks,
            POCloseDate: formatDateForAPI(closeDate),
            RoleId: roleId,
            CreatedBy: userName,
        }));
    };

    const isBusy = saveLoading;
    const showMain = !!poDetails;
    const showSummary = showMain && closeDate && remarks.trim();
    const canSubmit = showSummary && !isBusy;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 p-4 md:p-6">

            <div className="max-w-7xl mx-auto mb-6">
                <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-violet-700 shadow-xl shadow-indigo-500/20 p-7 text-white">
                    <div className="absolute inset-0 opacity-10"
                        style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '30px 30px' }} />
                    <div className="absolute top-0 right-0 w-72 h-72 bg-purple-500 rounded-full -translate-y-1/2 translate-x-1/4 opacity-20 blur-3xl" />
                    <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center shadow-lg border border-white/20">
                                <Lock className="h-7 w-7 text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="text-xs font-bold text-indigo-200 uppercase tracking-wider bg-white/10 px-2 py-0.5 rounded-full">Purchase Module</span>
                                </div>
                                <h1 className="text-2xl md:text-3xl font-black tracking-tight">
                                    {menuData?.name || 'Service Provider PO Close'}
                                </h1>
                                <p className="text-indigo-200 text-sm mt-0.5">Close an existing Service Provider Purchase Order</p>
                            </div>
                        </div>
                        <div className="hidden sm:flex items-center gap-3 text-indigo-200">
                            <div className="text-right">
                                <p className="text-xs uppercase tracking-wider">Module</p>
                                <p className="text-sm font-bold text-white">Purchase / SPPO Close</p>
                            </div>
                            <Navigation className="h-5 w-5 opacity-60" />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto space-y-6">

                {/* ── Section 1: Selection ──────────────────────────────── */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                    <SectionHeader icon={Lock} title="Select PO" subtitle="Cost center, vendor and SPPO number"
                        action={
                            <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                <RotateCcw className="h-3.5 w-3.5" /> Reset
                            </button>
                        }
                    />
                    <div className="p-6 md:p-8">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <Label required>Cost Center</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={ccCode} onChange={(e) => setCcCode(e.target.value)} disabled={ccLoading || isBusy}>
                                        <option value="">{ccLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {costCenters.map((c) => <option key={c.CC_Code} value={c.CC_Code}>{c.CC_Code} — {c.CC_Name}</option>)}
                                    </select>
                                    <SelectIcon loading={ccLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>Vendor</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={vendorCode} onChange={(e) => setVendorCode(e.target.value)} disabled={!ccCode || vendorsLoading || isBusy}>
                                        <option value="">{!ccCode ? 'Select cost center first' : vendorsLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {vendors.map((v) => <option key={v.VendorCode} value={v.VendorCode}>{v.VendorName}</option>)}
                                    </select>
                                    <SelectIcon loading={vendorsLoading} />
                                </div>
                            </div>
                            <div>
                                <Label required>SPPO No</Label>
                                <div className="relative">
                                    <select className={`${inputCls} appearance-none pr-10`} value={sppoNo} onChange={(e) => setSppoNo(e.target.value)} disabled={!vendorCode || poNumbersLoading || isBusy}>
                                        <option value="">{!vendorCode ? 'Select vendor first' : poNumbersLoading ? 'Loading…' : '-Please Select-'}</option>
                                        {poNumbers.map((p) => <option key={p.SPPONo} value={p.SPPONo}>{p.SPPONoDesc}</option>)}
                                    </select>
                                    <SelectIcon loading={poNumbersLoading} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {poDetailsLoading && (
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-10 flex items-center justify-center text-gray-400">
                        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading PO details…
                    </div>
                )}

                {showMain && (
                    <>
                        {/* ── Section 2: PO Info ───────────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={FileEdit} title="PO Information" />
                            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-4 gap-6">
                                <div>
                                    <Label>Start Date</Label>
                                    <input type="text" readOnly value={poDetails.SPPOStartDate || ''} className={inputCls} />
                                </div>
                                <div>
                                    <Label>End Date</Label>
                                    <input type="text" readOnly value={poDetails.SPPOEndDate || ''} className={inputCls} />
                                </div>
                                <div>
                                    <Label>Total Value</Label>
                                    <input type="text" readOnly value={`₹ ${formatIndianCurrency(poDetails.TotalValue)}`} className={inputCls} />
                                </div>
                                <div>
                                    <Label>Balance</Label>
                                    <input type="text" readOnly value={`₹ ${formatIndianCurrency(poDetails.Balance)}`} className={inputCls} />
                                </div>
                            </div>
                        </div>

                        {/* ── Section 3: Close Details ─────────────────────── */}
                        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                            <SectionHeader icon={Lock} title="Close Details" subtitle="Closing date and remarks" />
                            <div className="p-6 md:p-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <Label required>Closing Date</Label>
                                    <CustomDatePicker
                                        value={closeDate} onChange={setCloseDate} disabled={isBusy}
                                        minDate={parseAPIDate(poDetails.SPPOStartDate)}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <Label required>Remarks</Label>
                                    <textarea rows={2} className={`${inputCls} resize-none`} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={isBusy} />
                                </div>
                            </div>
                        </div>

                        {showSummary && (
                            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
                                <SectionHeader icon={CheckCircle} title="Close Summary" subtitle="Review before submitting" />
                                <div className="p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                                    {[
                                        { label: 'SPPO No', value: sppoNo },
                                        { label: 'Vendor', value: vendors.find((v) => v.VendorCode === vendorCode)?.VendorName },
                                        { label: 'Balance', value: `₹ ${formatIndianCurrency(poDetails.Balance)}` },
                                        { label: 'Closing Date', value: formatDateForAPI(closeDate) },
                                        { label: 'Created By', value: userName },
                                        { label: 'Remarks', value: remarks, wide: true },
                                    ].map(({ label, value, wide }) => (
                                        <div key={label} className={`bg-gray-50 dark:bg-gray-700/50 rounded-xl p-3 ${wide ? 'md:col-span-3' : ''}`}>
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
                                            <p className="font-semibold text-gray-800 dark:text-gray-100 break-words">{value ?? '—'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-end gap-3 pb-6">
                            <button onClick={handleFullReset} disabled={isBusy} className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border border-gray-200 dark:border-gray-600 disabled:opacity-50">
                                <RotateCcw className="h-4 w-4" /> Reset
                            </button>
                            <button onClick={handleSubmit} disabled={!canSubmit} className="flex items-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md shadow-indigo-500/30 hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all">
                                {saveLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <><Send className="h-4 w-4" /> Submit</>}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SPPOClose;
